import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Wallet } from '~/features/auth/types'
import { useBalance, useWithdrawCreate, useWithdrawStrategy } from '../api'
import { useWithdrawUserRequests } from '../api/useWithdrawUserRequests'
import { useBankGates, useCurrency } from '../hooks'
import { Br1TokenCurrency } from '../types'
import { CurrencyInput } from './CurrencyInput'
import { SelectCurrency } from './SelectCurrency'
import { UserWalletSelect } from './UserWalletSelect'
import { WithdrawRequestList } from './WithdrawRequestList'
const hexagonExplanation = new URL('../assets/hexagon-explanation.png', import.meta.url).toString()

const Highlight = ({ children, duration = 2000 }) => {
    const [show, setShow] = useState<boolean>(true)
    const [data, setData] = useState<any>(children)
    useEffect(() => {
        setTimeout(() => setShow(false), 100)
        setTimeout(() => setData(<>&nbsp;</>), duration)
    }, [])
    return (
        <div
            className={twMerge('animate transition-colors', show && 'bg-white/10')}
            style={{ transitionDuration: `${duration}ms` }}
        >
            {data}
        </div>
    )
}

const WithdrawForm = () => {
    const { currency } = useCurrency()
    const { data: usdc } = useBalance()
    const { withdrawBlocked } = useBankGates()
    const { data: withdrawStrategy, isError: strategyFailed } = useWithdrawStrategy(currency)
    const [wallet, setWallet] = useState<Wallet>()
    const [amount, setAmount] = useState<number>(0)
    const [targetAmount, setTargetAmount] = useState<number>(0)
    const [validationError, setValidationError] = useState<string>()
    const { mutate, error, isLoading, isSuccess, data, reset } = useWithdrawCreate()
    const { data: withdrawRequests } = useWithdrawUserRequests({ page: 1 })

    // Safe to read .fee unguarded: the form does not render until the strategy
    // has loaded, so nothing below runs against an undefined fee.
    const calculateTargetAmount = (value) => Math.round(value * (1 - withdrawStrategy.fee) * 100) / 100
    const calculateTokenAmount = (value) => Math.round(value * (1 / (1 - withdrawStrategy.fee)) * 100) / 100

    const tokenChangeHandler = (e) => {
        const value = e.target.value
        setAmount(value)
        setTargetAmount(calculateTargetAmount(value))
    }

    const targetChangeHandler = (e) => {
        const value = e.target.value
        setTargetAmount(value)
        setAmount(calculateTokenAmount(value))
    }

    const setPercentage = (percentage: number) => () => {
        let amount = Math.floor((usdc?.usdc ?? 0) * percentage) / 100
        setAmount(amount)
        setTargetAmount(calculateTargetAmount(amount))
    }

    const validate = () => {
        if (!wallet) {
            setValidationError('Select wallet')
            return false
        }
        if (amount > (usdc?.usdc ?? 0)) {
            setValidationError('Insufficient balance')
            return false
        } else if (amount < withdrawStrategy.min_amount) {
            // Threshold and message come from the server, which is the thing that actually
            // enforces it. They had drifted twice before: the message quoted a different
            // number than the check, and the check itself allowed amounts the server refused.
            setValidationError(`Minimum withdrawal amount: $${withdrawStrategy.min_amount}`)
            return false
        }
        return true
    }

    const submit = () => {
        reset()
        if (withdrawBlocked) {
            setValidationError(withdrawBlocked)
            return
        }
        if (validate()) {
            mutate({ wallet: wallet.pubkey, target_currency: currency.name, amount: amount })
        }
    }

    const clear = () => {
        setAmount(0)
        setTargetAmount(0)
    }

    useEffect(() => {
        setValidationError('')
    }, [amount])

    useEffect(() => {
        if (isSuccess) {
            clear()
        }
    }, [isSuccess])

    // Below every hook, so the hook order never changes across renders.
    //
    // The form stays hidden until the fee is known rather than rendering against a
    // placeholder: a wrong fee on screen is worse than a brief spinner, because the
    // player agrees to a deduction and gets a different one.
    if (strategyFailed) {
        return (
            <div className='col-start-1 col-end-[-1] text-xs tracking-wide text-center text-red-500 p-4'>
                Withdrawals are unavailable right now. Try again shortly.
            </div>
        )
    }
    if (!withdrawStrategy) {
        return (
            <div className='col-start-1 col-end-[-1] text-center p-4'>
                <FontAwesomeIcon icon={faSpinner} spin />
            </div>
        )
    }

    return (
        <div className='grid grid-cols-1 md:grid-cols-[1fr,max-content,1fr] col-start-1 col-end-[-1] gap-2'>
            <div className='col-start-1 col-end-[-1]'>
                <UserWalletSelect onSelect={(wallet) => setWallet(wallet)} />
            </div>
            <CurrencyInput currency={Br1TokenCurrency} value={amount.toString()} onChange={tokenChangeHandler} />
            {/* Single column on mobile turns this into its own row, so centre it there. */}
            <div className='flex flex-row items-center justify-center md:justify-start px-2 font-bold'>=</div>
            <CurrencyInput currency={currency} value={targetAmount.toString()} onChange={targetChangeHandler} />
            {/* These were sized in em fractions of an already-small parent, landing near 7px on a phone. */}
            <div className='row-start-3 md:row-start-auto grid grid-cols-4 gap-1 text-xs'>
                {[25, 50, 75, 100].map((percentage) => (
                    <button
                        key={percentage}
                        className='bg-white/20 hover:bg-white/30 hover:outline outline-white/50 min-h-[40px] font-bold transition-colors'
                        onClick={setPercentage(percentage)}
                    >
                        {percentage} %
                    </button>
                ))}
            </div>
            <div className='col-start-[-2] text-xs text-white/60 flex items-center'>
                {/* Rounded: 0.015 * 100 is 1.5000000000000002 in binary floating point. */}
                Fee: {Math.round(withdrawStrategy.fee * 10000) / 100} %
            </div>

            <div className='col-start-1 col-end-[-1] bg-black-gradient text-xs p-3 my-2 flex flex-row gap-2 items-center'>
                <img src={hexagonExplanation} className='h-4 w-4 shrink-0' />
                <div className='tracking-wide text-white/70'>
                    Withdrawal requests take approximately 7 days to process.
                </div>
            </div>

            {/* Disabled as well as guarded in `submit`: the form is reachable from more than one
                entry point, and a button that looks live and then refuses reads as a broken site
                rather than as a hold on the account. */}
            <button
                className='col-start-1 col-end-[-1] bg-br1-gradient p-4 min-h-[48px] font-black hover:outline disabled:opacity-40 disabled:hover:outline-none'
                onClick={submit}
                disabled={!!withdrawBlocked}
                title={withdrawBlocked}
            >
                REQUEST WITHDRAWAL {isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
            </button>

            <div
                className={twMerge(
                    'col-start-1 col-end-[-1] text-xs tracking-wide text-center',
                    isSuccess ? 'text-green-500' : 'text-red-500'
                )}
            >
                {validationError ||
                    error?.error?.message ||
                    (isSuccess && <Highlight>Withdrawal request submitted.</Highlight>) || <span>&nbsp;</span>}
            </div>
            {withdrawRequests?.count > 0 && (
                <div className='col-start-1 col-end-[-1]'>
                    <WithdrawRequestList.UserTemplate />
                </div>
            )}
        </div>
    )
}

export const Withdraw = () => {
    const { currency } = useCurrency()
    return <>{currency ? <WithdrawForm /> : <SelectCurrency />}</>
}
